import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import {
  TICKET_ART_LABEL,
  fetchDashboardSummary,
  fetchTicketsJeArt,
  type TicketArt,
  type TicketArtZahlen,
} from "@/lib/api"

// Zuordnung TicketArt (snake_case, Filter-Query-Wert) → TicketArtZahlen-Feld
// (camelCase, Pydantic-Alias) — bewusst explizit statt String-Transformation.
const ART_ZU_FELD: Record<TicketArt, keyof TicketArtZahlen> = {
  wartung_inspektion: "wartungInspektion",
  pruefung: "pruefung",
  aufgabe: "aufgabe",
  ereignis: "ereignis",
  auftrag: "auftrag",
}

// 02-Regel 38: Handbuch "Maschinendatenbank" (02-S) war in Ninox eine leere
// Navigationstabelle ohne eigene Felder — reiner Deep-Link zur Dokumentation.
// Ziel ist BookStack (dokubase.rmnc.app, Muster reference_dokubase_bookstack),
// statische Konfigurationskonstante ohne Fachlogik.
const HANDBUCH_URL = "https://dokubase.rmnc.app/books/technik-instandhaltung"

/**
 * Startseiten-Dashboard (AP-3.5) — ersetzt 02-GB/CB/FB (Kachelmenü + Filter-
 * Hubs) durch reinen Frontend-Zustand + eine Live-Aggregation (KEINE
 * Pro-Nutzer-Tabellen, ARCHITEKTUR.md E4).
 */
export function DashboardPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: fetchDashboardSummary,
  })
  const { data: ticketsJeArt } = useQuery({
    queryKey: ["dashboard-tickets-je-art"],
    queryFn: fetchTicketsJeArt,
  })

  return (
    <div>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Übersicht</h1>
          <p className="mt-1 text-sm text-slate-600">
            Kennzahlen aus Prüfungen, Wartungen und offenen Servicetickets.
          </p>
        </div>
        <a
          href={HANDBUCH_URL}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          📖 Handbuch
        </a>
      </header>

      {isLoading && <p className="text-sm text-slate-500">Lade…</p>}
      {isError && <p className="text-sm text-red-700">Dashboard-Daten nicht verfügbar.</p>}

      {data && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <Kachel label="Anlagen" wert={data.anlagenGesamt} to="/maschinen" />
          <Kachel
            label="Prüfungen (rot)"
            wert={data.pruefungenRot}
            to="/maschinen/pruefungen"
            farbe="bg-red-50 text-red-800"
          />
          <Kachel
            label="Prüfungen (orange)"
            wert={data.pruefungenOrange}
            to="/maschinen/pruefungen"
            farbe="bg-orange-50 text-orange-800"
          />
          <Kachel
            label="Wartungen (rot)"
            wert={data.wartungenRot}
            to="/maschinen/wartungen"
            farbe="bg-red-50 text-red-800"
          />
          <Kachel
            label="Wartungen (orange)"
            wert={data.wartungenOrange}
            to="/maschinen/wartungen"
            farbe="bg-orange-50 text-orange-800"
          />
          <Kachel label="Offene Tickets" wert={data.ticketsOffen} to="/tickets" />
        </div>
      )}

      {ticketsJeArt && (
        <div className="mt-8">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">
            Offene Tickets je Art (G.2-G.5-Äquivalent)
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {(Object.keys(TICKET_ART_LABEL) as TicketArt[]).map((art) => (
              <Link
                key={art}
                to={`/tickets?art=${art}`}
                className="rounded-lg bg-slate-100 p-4 text-slate-800 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                <div className="text-2xl font-semibold">{ticketsJeArt[ART_ZU_FELD[art]]}</div>
                <div className="text-xs">{TICKET_ART_LABEL[art]}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Link
          to="/maschinen/pruefungen"
          className="rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 p-6 text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          <div className="text-2xl">🔧</div>
          <div className="mt-2 text-lg font-semibold">Prüfung &amp; Wartung &amp; Inspektion</div>
          <div className="text-sm text-blue-100">Tabelle öffnen</div>
        </Link>
        <Link
          to="/maschinen"
          className="rounded-lg bg-gradient-to-br from-green-600 to-green-800 p-6 text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          <div className="text-2xl">🗂️</div>
          <div className="mt-2 text-lg font-semibold">Stammdaten</div>
          <div className="text-sm text-green-100">Tabelle öffnen</div>
        </Link>
      </div>
    </div>
  )
}

function Kachel({
  label,
  wert,
  to,
  farbe = "bg-slate-50 text-slate-800",
}: {
  label: string
  wert: number
  to: string
  farbe?: string
}) {
  return (
    <Link
      to={to}
      className={`rounded-lg p-4 ${farbe} hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1`}
    >
      <div className="text-2xl font-semibold">{wert}</div>
      <div className="text-xs">{label}</div>
    </Link>
  )
}
